import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from './SuperAdminAuth';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export const SuperAdminLoginPage = () => {
  const { login, user } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  React.useEffect(() => {
    if (user) navigate('/superadmin', { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) {
      toast.success('Welcome back');
      navigate('/superadmin', { replace: true });
    } else {
      toast.error(result.error || 'Login failed');
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      if (r.ok) {
        toast.success('If the email exists, a reset link has been sent.');
        setForgotOpen(false);
        setForgotEmail('');
      } else {
        toast.error('Could not process request');
      }
    } catch {
      toast.error('Network error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="super-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4">
            <Shield className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white">Super Admin</h1>
          <p className="text-sm text-slate-400 mt-1">Opswarex Platform Control</p>
        </div>

        {!forgotOpen ? (
          <form onSubmit={onSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full mt-1 px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
                placeholder="you@opswarex.com"
                data-testid="super-login-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Password</label>
              <div className="relative mt-1">
                <input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-slate-950 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
                  placeholder="••••••••"
                  data-testid="super-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium disabled:opacity-50"
              data-testid="super-login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-slate-500 hover:text-amber-400"
                data-testid="super-login-forgot"
              >
                Forgot password?
              </button>
              <a href="/login" className="text-slate-500 hover:text-amber-400">Tenant login →</a>
            </div>
          </form>
        ) : (
          <form onSubmit={onForgot} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
            <div className="text-sm text-slate-300">Enter your email and we'll send a reset link.</div>
            <input
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
              placeholder="you@opswarex.com"
            />
            <div className="flex gap-2">
              <button type="button" onClick={() => setForgotOpen(false)} className="flex-1 py-3 rounded-lg border border-slate-800 text-slate-300 hover:bg-slate-800">Cancel</button>
              <button type="submit" className="flex-1 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium">Send reset link</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
